import { ref, readonly } from 'vue';

/**
 * Screen reader announcer composable for dynamic content.
 * Creates announcements that will be picked up by screen readers
 * using ARIA live regions.
 *
 * @example
 * ```ts
 * const { announce } = useAnnouncer();
 *
 * // Polite announcement (waits for current speech to finish)
 * announce('Page loaded successfully');
 *
 * // Assertive announcement (interrupts current speech)
 * announce('Error: Form submission failed', 'assertive');
 * ```
 */

// Shared state across all instances for global announcements
const globalMessage = ref('');
const globalPoliteness = ref<'polite' | 'assertive'>('polite');

export function useAnnouncer() {
  /**
   * Announce a message to screen readers.
   *
   * @param text - The message to announce
   * @param mode - 'polite' waits for current speech, 'assertive' interrupts immediately
   */
  function announce(text: string, mode: 'polite' | 'assertive' = 'polite') {
    // Clear the message first to ensure the change is detected
    // even if announcing the same message twice
    globalMessage.value = '';
    globalPoliteness.value = mode;

    // Use setTimeout to ensure the screen reader picks up the change
    // The DOM needs to update with empty string first, then with new content
    setTimeout(() => {
      globalMessage.value = text;
    }, 100);
  }

  /**
   * Clear the current announcement.
   */
  function clear() {
    globalMessage.value = '';
  }

  /**
   * Announce a success message (polite).
   */
  function announceSuccess(text: string) {
    announce(text, 'polite');
  }

  /**
   * Announce an error message (assertive).
   */
  function announceError(text: string) {
    announce(text, 'assertive');
  }

  /**
   * Announce a loading state.
   */
  function announceLoading(text: string = 'Loading...') {
    announce(text, 'polite');
  }

  /**
   * Announce page navigation.
   */
  function announceNavigation(pageName: string) {
    announce(`Navigated to ${pageName}`, 'polite');
  }

  return {
    message: readonly(globalMessage),
    politeness: readonly(globalPoliteness),
    announce,
    clear,
    announceSuccess,
    announceError,
    announceLoading,
    announceNavigation,
  };
}
